import api from './client'

export interface AssignmentChoice {
  id: number
  choice_label: string
  choice_text: string
  is_correct: boolean
  explanation: string | null
  display_order: number
}

export interface AssignedStudentBrief {
  student_id: number
  student_name: string | null
}

export interface Assignment {
  id: number
  uuid?: string
  tutor_id: number | null
  title: string
  description: string | null
  action?: string
  assignment_type: string
  grading_mode: 'auto' | 'manual'
  due_date: string | null
  base_xp: number
  is_active: boolean
  route?: string | null
  action_params?: string | null
  question_count: number
  status: string
  total_assigned_students: number
  processing_students_count: number
  submitted_students_count: number
  graded_students_count: number
  create_datetime: string | null
  assigned_students?: AssignedStudentBrief[]
}

export interface AssignmentStudent {
  id: number
  assignment_id: number
  student_id: number
  student_name: string | null
  student_email: string | null
  status: string
  started_at: string | null
  submitted_at: string | null
  graded_at: string | null
  score: number | null
  xp_earned: number
  tutor_feedback: string | null
}

export interface StudentAssignmentItem {
  assignment: Assignment
  status: string
  score: number | null
  xp_earned: number
  tutor_feedback?: string | null
}

export interface AssignmentQuestion {
  id: number
  mastery_id: number
  difficulty_level: number
  passage_text: string | null
  passage_images: string[]
  prompt: string
  prompt_image: string | null
  explanation: string | null
  question_type: 'multiple_choice' | 'spr'
  correct_answer_text: string | null
  choices: AssignmentChoice[]
}

export interface SubmissionQuestion extends AssignmentQuestion {
  selected_choice_id: number | null
  correct_choice_id: number | null
  /** Letter/label from DB when choice row is resolved (works if id matching fails) */
  selected_choice_label?: string | null
  correct_choice_label?: string | null
  text_answer: string | null
  is_correct: boolean | null
  time_spent_seconds: number
}

export interface AssignmentDetailResponse {
  assignment: Assignment
  questions: AssignmentQuestion[]
  students: AssignmentStudent[]
}

export interface SubmissionDetailResponse {
  assignment: Assignment
  student: AssignmentStudent
  auto_score: number
  can_view_answers: boolean
  questions: SubmissionQuestion[]
}

export interface AnswerSubmit {
  question_set_id: number
  selected_choice_id: number | null
  text_answer?: string
  time_spent_seconds: number
}

export interface StudentSummaryRow {
  student_id: number
  student_name: string
  total: number
  assigned: number
  in_progress: number
  completed: number
}

export interface FlatAssignmentRow {
  assignment_student_id: number
  assignment_id: number
  title: string
  description: string | null
  assignment_type: string
  grading_mode: 'auto' | 'manual'
  due_date: string | null
  question_count: number
  route: string | null
  action_params: string | null
  student_id: number
  student_name: string
  status: string
  score: number | null
  submitted_at: string | null
}

export const assignmentsApi = {
  list: () => api.get<Assignment[] | StudentAssignmentItem[]>('/assignments'),
  summary: () => api.get<StudentSummaryRow[]>('/assignments/summary'),
  flatRows: () => api.get<FlatAssignmentRow[]>('/assignments/flat-rows'),
  get: (id: number) =>
    api.get<AssignmentDetailResponse>(
      `/assignments/${id}`
    ),
  getSubmission: (assignmentId: number, studentId: number) =>
    api.get<SubmissionDetailResponse>(`/assignments/${assignmentId}/students/${studentId}`),
  create: (data: {
    title: string
    description?: string
    action?: string
    assignment_type: string
    grading_mode: 'auto' | 'manual'
    due_date?: string | null
    base_xp: number
    route?: string | null
    action_params?: string | null
    portion_id?: number
    domain_id?: number
    mastery_id?: number
    difficulty_level?: number
    /** Omit for zero-question assignments (e.g. action `watch`). */
    question_count?: number
    insert_plan_id?: number
    insert_position?: number
    insert_append?: boolean
  }) => api.post<{ id: number }>('/assignments', data),
  assign: (id: number, data: { student_ids?: number[]; roster_id?: number }) =>
    api.post(`/assignments/${id}/assign`, data),
  start: (id: number) => api.post(`/assignments/${id}/start`),
  submit: (id: number, answers: AnswerSubmit[]) =>
    api.post<{
      message: string
      status: string
      score: number | null
      result?: SubmissionDetailResponse
    }>(`/assignments/${id}/submit`, { answers }),
  grade: (id: number, data: { student_id: number; score?: number; tutor_feedback?: string; answer_overrides?: any[] }) =>
    api.post(`/assignments/${id}/grade`, data),
}
